import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import {
  goodie as goodieHelpers,
  dateTime,
  NaiveDateTime
} from '@guestgoodie/helpers'
import { Cancel01Icon, Tick01Icon } from '@hugeicons/core-solid-rounded'
import * as Dialog from '@radix-ui/react-dialog'
import { Trans, useLingui } from '@lingui/react/macro'

import { dialogContainer, DialogHeader } from '@/layouts/Dialog'
import * as Form from '@/features/form'
import { CheckPermission, Permission } from '@/features/auth'
import Button from '@/components/ui/Button'
import DateTimeInput from '@/components/ui/DateTimeInput'
import Textarea from '@/components/ui/Textarea'
import { useAddRecentlyRead } from '../hooks/useOrdersSubscriptions'

type FormData = {
  responseMessage: string
  default: {
    start: Date
  }
}

function ApproveResponseContent({
  order,
  goodie,
  space,
  close
}: {
  order: GraphQL.OrderFragmentFragment
  goodie: GraphQL.GoodieFragmentFragment
  space?: GraphQL.SpaceFragmentFragment | null
  close: () => void
}) {
  const { t } = useLingui()
  const addRecentlyRead = useAddRecentlyRead()
  const [approve] = useMutation(AdminAPI.order.APPROVE)

  const { canSelectTimes, serviceDuration } = useMemo(() => {
    if (goodie.__typename !== 'Default') {
      return { canSelectTimes: false, serviceDuration: null }
    }

    return {
      canSelectTimes: goodie.default.bookWithSuggestions,
      serviceDuration: goodie.default.duration ?? 0
    }
  }, [goodie])

  const onApprove = useCallback(
    async (data: FormData) => {
      if (!order.isRead) addRecentlyRead(order.id)

      return approve({
        variables: {
          input: {
            id: order.id,
            ...data
          }
        }
      }).then(() => close())
    },
    [order, approve, close, addRecentlyRead]
  )

  const goodieTitle = goodie?.title
  const spaceIdentifier = space?.identifier
  const goodieInfo = goodie && goodieHelpers.toInfoFromGoodieFragment(goodie)

  const defaultStart = useMemo(() => {
    if (order.__typename === 'OrderDefault') {
      const firstStart = order.default.guestDateRanges[0].start

      if (firstStart) return new Date(firstStart)
    }

    return new Date()
  }, [order])

  return (
    <Form.Root<FormData>
      onSubmit={onApprove}
      defaultValues={{
        responseMessage: '',
        default: {
          start: defaultStart
        }
      }}
    >
      <div className="flex flex-col gap-2">
        <DialogHeader
          title={t`Approve Request`}
          description={t({
            comment:
              'Description for the approving/rejecting of a request form',
            message: `Room ${spaceIdentifier}: ${goodieTitle} (${goodieInfo})`
          })}
          close={close}
        />
        <div className="flex flex-col gap-6 p-2.5">
          {order.__typename === 'OrderDefault' && (
            <div className="flex flex-col gap-1">
              <Form.Field
                name="default.start"
                label={t`Start time`}
                rules={{
                  required: t`This field is required`,
                  validate: {
                    insideGuestDateRanges(startDate) {
                      if (serviceDuration == null) return

                      const isValid = order.default.guestDateRanges.some(
                        function (range) {
                          const cmpStart = new Date(range.start)
                          const cmpEnd = new Date(
                            cmpStart.getTime() +
                              range.duration -
                              serviceDuration
                          )

                          return cmpStart <= startDate && cmpEnd >= startDate
                        }
                      )

                      return isValid
                        ? undefined
                        : t`Start date need to fit with what the guest was requesting.`
                    }
                  }
                }}
              >
                {({ renderProps: { onChange, readOnly, value, ...props } }) => (
                  <DateTimeInput
                    onChange={date => onChange(date.toZonedDate())}
                    value={NaiveDateTime.fromDateInTimezone(value)}
                    {...props}
                    readOnly={!canSelectTimes || readOnly}
                  />
                )}
              </Form.Field>
              {canSelectTimes && (
                <ul className="flex flex-col gap-2 px-5">
                  <span className="text-regular text-text">
                    <Trans comment="Label/Heading for a list of times at which the guest has time for the ordered goodie">
                      Possible times for the guest
                    </Trans>
                  </span>
                  {order.default.guestDateRanges.map(function (range) {
                    return (
                      <li
                        className="list-inside list-disc text-regular text-text"
                        key={range.start}
                      >
                        {dateTime.toRange({
                          isoDateTime: range.start,
                          duration: range.duration
                        })}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}
          <Form.Field name="responseMessage" label={t`Message`}>
            {({ renderProps: { onChange, ...props } }) => (
              <Textarea
                onChange={event => onChange(event.target.value)}
                placeholder={t`Message that is shown to the guest.`}
                {...props}
              />
            )}
          </Form.Field>
        </div>
        <div className="mt-4 flex w-full flex-row justify-between gap-2 p-2.5">
          <Button variant="outline" onClick={close}>
            <Trans>Cancel</Trans>
          </Button>
          <Form.Submit>
            <Trans>Approve</Trans>
          </Form.Submit>
        </div>
      </div>
    </Form.Root>
  )
}

function RejectResponseContent({
  order,
  goodie,
  space,
  close
}: {
  order: GraphQL.OrderFragmentFragment
  goodie: GraphQL.GoodieFragmentFragment
  space?: GraphQL.SpaceFragmentFragment | null
  close: () => void
}) {
  const { t } = useLingui()
  const addRecentlyRead = useAddRecentlyRead()
  const [reject] = useMutation(AdminAPI.order.REJECT)

  const onReject = useCallback(
    async (data: FormData) => {
      if (!order.isRead) addRecentlyRead(order.id)

      reject({
        variables: {
          input: {
            id: order.id,
            ...data
          }
        }
      }).then(() => close())
    },
    [reject, close, addRecentlyRead, order.id, order.isRead]
  )

  const goodieTitle = goodie?.title
  const spaceIdentifier = space?.identifier
  const goodieInfo = goodie && goodieHelpers.toInfoFromGoodieFragment(goodie)

  return (
    <Form.Root<FormData>
      onSubmit={onReject}
      defaultValues={{
        responseMessage: ''
      }}
    >
      <div className="flex flex-col gap-2">
        <DialogHeader
          title={t`Reject Request`}
          description={t({
            comment:
              'Description for the approving/rejecting of a request form',
            message: `Room ${spaceIdentifier}: ${goodieTitle} (${goodieInfo})`
          })}
          close={close}
        />
        <Form.Field name="responseMessage" label={t`Message`}>
          {({ renderProps: { onChange, ...props } }) => (
            <Textarea
              onChange={event => onChange(event.target.value)}
              placeholder={t`Message that is shown to the guest.`}
              {...props}
            />
          )}
        </Form.Field>
        <div className="mt-4 flex w-full flex-row justify-between gap-2">
          <Button variant="outline" onClick={close}>
            <Trans>Cancel</Trans>
          </Button>
          <Form.Submit>
            <Trans>Reject</Trans>
          </Form.Submit>
        </div>
      </div>
    </Form.Root>
  )
}

export default function Response({
  order
}: {
  order: GraphQL.OrderFragmentFragment
}) {
  const [openApprove, setOpenApprove] = useState(false)
  const [openReject, setOpenReject] = useState(false)

  const { data: goodieData } = useQuery(AdminAPI.goodie.GET, {
    variables: { id: order.goodieId }
  })

  const goodie = useMemo(
    () => getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, goodieData?.getGoodie),
    [goodieData]
  )

  const { data: stayData } = useQuery(AdminAPI.stay.GET, {
    variables: { id: order.stayId, withSpace: true }
  })

  const stay = useMemo(() => {
    return getFragment(SharedAPI.stay.STAY_FRAGMENT, stayData?.getStay)
  }, [stayData])

  const space = useMemo(() => {
    return getFragment(SharedAPI.space.SPACE_FRAGMENT, stay?.space)
  }, [stay])

  if (!goodie) return null

  return (
    <CheckPermission permissions={[Permission.ORDER_UPDATE]}>
      <div className="flex flex-col items-end gap-1">
        <Dialog.Root open={openApprove} onOpenChange={setOpenApprove}>
          <Dialog.Trigger asChild>
            <Button Icon={Tick01Icon}>
              <Trans>Approve</Trans>
            </Button>
          </Dialog.Trigger>
          <Dialog.Portal container={dialogContainer}>
            <Dialog.Overlay />
            <Dialog.Content className="absolute left-1/2 top-1/2 mx-auto w-screen max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-5 shadow-md z-dialog">
              <ApproveResponseContent
                order={order}
                goodie={goodie}
                space={space}
                close={() => setOpenApprove(false)}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
        <Dialog.Root open={openReject} onOpenChange={setOpenReject}>
          <Dialog.Trigger asChild>
            <Button Icon={Cancel01Icon} variant="outline">
              <Trans>Reject</Trans>
            </Button>
          </Dialog.Trigger>
          <Dialog.Portal container={dialogContainer}>
            <Dialog.Overlay />
            <Dialog.Content className="absolute left-1/2 top-1/2 mx-auto w-screen max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-5 shadow-md z-dialog">
              <RejectResponseContent
                order={order}
                goodie={goodie}
                space={space}
                close={() => setOpenReject(false)}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </CheckPermission>
  )
}
